// Ported from the renderVals() method of the original Ops Display.dc.html.

export const PAGE_TITLES = {
  home: ["Ops Overview", ""],
  sprint: ["Work Queue", ""],
  server: ["Platform Status", "5 platforms · 2 regions"],
  review: ["Code Review Queue", "12 open · 3 stale"],
  estimate: ["Estimate", "AI task breakdown"],
};

// Status emphasis ladder: on hold = red field (alarm), in progress = ink field
// (active), staging = ink outline (waiting), done = neutral tint (quiet).
const STATUS = {
  alarm: {
    bg: "var(--color-accent)",
    ink: "var(--on-accent)",
    border: "2px solid var(--color-accent)",
    dot: "var(--on-accent)",
  },
  active: {
    bg: "var(--color-text)",
    ink: "var(--color-bg)",
    border: "2px solid var(--color-text)",
    dot: "var(--color-bg)",
  },
  waiting: {
    bg: "transparent",
    ink: "var(--color-text)",
    border: "2px solid var(--color-text)",
    dot: "var(--color-text)",
  },
  quiet: {
    bg: "var(--color-neutral-200)",
    ink: "var(--color-neutral-800)",
    border: "2px solid var(--color-neutral-300)",
    dot: "var(--color-neutral-600)",
  },
};

const st = (row, key) => ({
  ...row,
  statusBg: STATUS[key].bg,
  statusInk: STATUS[key].ink,
  statusBorder: STATUS[key].border,
  statusDot: STATUS[key].dot,
});

// Maps a status label back to its emphasis key so status changes can
// re-derive pill colors at runtime.
const STATUS_KEY = {
  Backlog: "quiet",
  "In progress": "active",
  Staging: "waiting",
  "On hold": "alarm",
  Done: "quiet",
};

export const applyStatus = (task) => st(task, STATUS_KEY[task.status]);

export const TASK_STATUSES = [
  "Backlog",
  "In progress",
  "Staging",
  "On hold",
  "Done",
];

export const REPOS = [
  "core-api",
  "billing-service",
  "web-client",
  "infra-agent",
  "data-pipeline",
];

export const INITIAL_CONFIG_USERS = [
  { id: "1", name: "Ana Beltrán", initials: "AB", role: "admin", active: true },
  { id: "2", name: "Jun Park", initials: "JP", role: "tablet", active: true },
  { id: "3", name: "Marek Kowal", initials: "MK", role: "tablet", active: true },
  { id: "4", name: "Dana Reyes", initials: "DR", role: "tablet", active: true },
  { id: "5", name: "Iris Vogel", initials: "IV", role: "tablet", active: true },
  { id: "6", name: "Sofia Marín", initials: "SM", role: "tablet", active: true },
];

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export const todayLabel = () => {
  const d = new Date();
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

export const nextTaskId = (tasks) => {
  const max = tasks.reduce((m, t) => {
    const n = parseInt(t.id, 10);
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
  return String(max + 1);
};

export const initialsOf = (name) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const slugify = (str) =>
  str
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const initialTasks = [
  st({ id: "402", title: "Retry queue for payment webhooks", owner: "Dana Reyes", initials: "DR", status: "On hold", repos: ["core-api", "billing-service"], description: "Webhook deliveries fail silently when Stripe retries overlap, dropping confirmed payment events. Add an idempotent retry queue with exponential backoff.", created: "14 Aug", updated: "29 Aug", branch: "ATL-402/webhook-retry-queue" }, "alarm"),
  st({ id: "407", title: "Deprecate legacy v1 auth endpoints", owner: "Marek Kowal", initials: "MK", status: "Done", repos: ["core-api"], description: "Sunset the v1 /auth endpoints behind a deprecation window and redirect callers to the v2 token flow.", created: "02 Aug", updated: "26 Aug", branch: "ATL-407/deprecate-v1-auth" }, "quiet"),
  st({ id: "410", title: "Session token rotation on refresh", owner: "Ana Beltrán", initials: "AB", status: "Staging", repos: ["core-api"], description: "Rotate refresh tokens on every use and revoke the previous one to mitigate replay attacks.", created: "09 Aug", updated: "28 Aug", branch: "ATL-410/token-rotation" }, "waiting"),
  st({ id: "411", title: "Bulk export to Parquet", owner: "Jun Park", initials: "JP", status: "In progress", repos: ["data-pipeline", "core-api", "web-client"], description: "Add a streaming Parquet writer for bulk exports so large reports no longer hold rows in memory.", created: "11 Aug", updated: "01 Sep", branch: "ATL-411/parquet-writer" }, "active"),
  st({ id: "414", title: "Audit log retention policy", owner: "Sofia Marín", initials: "SM", status: "In progress", repos: ["core-api"], description: "Define and enforce the retention window for audit logs, including archive and purge jobs.", created: "13 Aug", updated: "01 Sep", branch: "ATL-414/audit-retention" }, "active"),
  st({ id: "415", title: "Cache VRAM telemetry per node", owner: "Jun Park", initials: "JP", status: "Staging", repos: ["infra-agent"], description: "Cache per-node VRAM telemetry to cut repeated agent polling on the dashboards.", created: "15 Aug", updated: "30 Aug", branch: "ATL-415/vram-telemetry-cache" }, "waiting"),
  st({ id: "417", title: "Users table migration on staging", owner: "Marek Kowal", initials: "MK", status: "On hold", repos: ["core-api", "infra-agent"], description: "Migrate the users table to the new schema on staging without locking writes.", created: "18 Aug", updated: "27 Aug", branch: "ATL-417/users-migration" }, "alarm"),
  st({ id: "419", title: "Tabular numerals in metric strip", owner: "Ana Beltrán", initials: "AB", status: "Done", repos: ["web-client"], description: "Apply tabular numerals across the metric strip so values stop shifting width on updates.", created: "20 Aug", updated: "25 Aug", branch: "ATL-419/tabular-numerals" }, "quiet"),
  st({ id: "421", title: "Tablet display breakpoints", owner: "Iris Vogel", initials: "IV", status: "Done", repos: ["web-client"], description: "Add tablet-range breakpoints so the layout stays legible on mid-size displays.", created: "22 Aug", updated: "24 Aug", branch: "ATL-421/tablet-breakpoints" }, "quiet"),
];

export const countByStatus = (list, status) =>
  list.filter((t) => t.status === status).length;

export const sprintKicker = (list) =>
  `${countByStatus(list, "In progress")} active · ${countByStatus(list, "On hold")} on hold`;

export const overviewKicker = (platforms, tasks, prs) => {
  const alerts =
    platforms.filter((p) => p.healthKey !== "up").length +
    countByStatus(tasks, "On hold") +
    prs.filter((p) => p.stale).length;
  const up = platforms.filter((p) => p.healthKey === "up").length;
  return `${alerts} alert${alerts === 1 ? "" : "s"} · ${up}/${platforms.length} up`;
};

const deployRaw = [
  { time: "09:12", name: "api-gateway v4.18.2", duration: "3m 41s", ok: true },
  { time: "08:47", name: "web-client v2.31.0", duration: "6m 02s", ok: true },
  { time: "07:55", name: "inference-worker v1.9.4", duration: "1m 18s", ok: false },
  { time: "07:20", name: "billing-service v3.4.7", duration: "4m 56s", ok: true },
  { time: "06:38", name: "search-indexer v0.22.1", duration: "2m 09s", ok: true },
];
export const deploys = deployRaw.map((d) => ({ ...d, failed: !d.ok }));

const HEALTH = {
  up: { label: "Operational", bg: "var(--color-text)", ink: "var(--color-bg)", dot: "var(--color-bg)" },
  degraded: { label: "Degraded", bg: "var(--color-accent)", ink: "var(--on-accent)", dot: "var(--on-accent)" },
  down: { label: "Down", bg: "var(--color-accent)", ink: "var(--on-accent)", dot: "var(--on-accent)" },
  unknown: { label: "Unknown", bg: "var(--color-neutral-300)", ink: "var(--color-neutral-700)", dot: "var(--color-neutral-700)" },
};

// Maps a live Platform's healthStatus enum to the same {label,bg,ink,dot}
// display fields the static demo data used to bake in at module load.
export const applyHealth = (platform) => ({
  ...platform,
  ...(HEALTH[platform.healthStatus] ?? HEALTH.unknown),
  healthKey: platform.healthStatus ?? "unknown",
});

export const platforms = [
  {
    name: "Core API",
    region: "eu-west",
    version: "v4.18.2",
    ...HEALTH.up,
    uptime: "99.98%",
    p95: "120ms",
    errorRate: "0.02%",
    lastDeploy: "Today 09:12",
    deployOk: true,
    incidents: 0,
    activeUsers: 1284,
    deps: 3,
    depsUp: 3,
    cronOk: true,
    sslDays: 87,
  },
  {
    name: "Web Client",
    region: "eu-west",
    version: "v2.31.0",
    ...HEALTH.up,
    uptime: "100%",
    p95: "340ms",
    errorRate: "0.00%",
    lastDeploy: "Today 08:47",
    deployOk: true,
    incidents: 0,
    activeUsers: 3421,
    deps: 2,
    depsUp: 2,
    cronOk: true,
    sslDays: 87,
  },
  {
    name: "Inference Worker",
    region: "us-east",
    version: "v1.9.4",
    ...HEALTH.degraded,
    uptime: "97.42%",
    p95: "2.1s",
    errorRate: "4.18%",
    lastDeploy: "Today 07:55",
    deployOk: false,
    incidents: 1,
    activeUsers: 89,
    deps: 4,
    depsUp: 3,
    cronOk: false,
    sslDays: 52,
  },
  {
    name: "Billing Service",
    region: "eu-west",
    version: "v3.4.7",
    ...HEALTH.up,
    uptime: "99.91%",
    p95: "210ms",
    errorRate: "0.09%",
    lastDeploy: "Today 07:20",
    deployOk: true,
    incidents: 0,
    activeUsers: 412,
    deps: 5,
    depsUp: 5,
    cronOk: true,
    sslDays: 124,
  },
  {
    name: "Search Indexer",
    region: "us-east",
    version: "v0.22.1",
    ...HEALTH.down,
    uptime: "88.14%",
    p95: "—",
    errorRate: "62.30%",
    lastDeploy: "Today 06:38",
    deployOk: true,
    incidents: 2,
    activeUsers: 0,
    deps: 3,
    depsUp: 1,
    cronOk: false,
    sslDays: 31,
  },
].map((p) => ({ ...p, healthKey: HEALTH.up.label === p.label ? "up" : HEALTH.degraded.label === p.label ? "degraded" : "down" }));

const prRaw = [
  st({ title: "Harden webhook signature verification", repo: "core-api", author: "Dana Reyes", initials: "DR", status: "Changes requested", wait: "74h", stale: true }, "alarm"),
  st({ title: "Replace polling with SSE on dashboards", repo: "web-client", author: "Iris Vogel", initials: "IV", status: "Awaiting review", wait: "61h", stale: true }, "alarm"),
  st({ title: "Drop legacy v1 auth endpoints", repo: "core-api", author: "Marek Kowal", initials: "MK", status: "Awaiting review", wait: "52h", stale: true }, "alarm"),
  st({ title: "Cache VRAM telemetry per node", repo: "infra-agent", author: "Jun Park", initials: "JP", status: "1 approval", wait: "19h", stale: false }, "active"),
  st({ title: "Parquet writer for bulk export", repo: "data-pipeline", author: "Sofia Marín", initials: "SM", status: "In review", wait: "8h", stale: false }, "waiting"),
  st({ title: "Tabular numerals in metric strip", repo: "web-client", author: "Ana Beltrán", initials: "AB", status: "Awaiting review", wait: "3h", stale: false }, "quiet"),
];
export const prs = prRaw.map((p) => ({ ...p, fresh: !p.stale }));
